import type { FC } from 'react';
import React, { Fragment, memo, useCallback } from 'react';
import { styled } from '@compiled/react';
import type { IntlShape } from 'react-intl-next';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import Spinner from '@atlaskit/spinner/spinner';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	HOME_SPACES_EXPERIENCE,
	ExperienceSuccess,
	ExperienceFailure,
} from '@confluence/experience-tracker';
import { useSpacesData } from '@confluence/space-utils/entry-points/useSpacesData';
import {
	HoverableItemViewContainer as SpacesItemContainer,
	EmptyContainer,
	LoadingContainer,
	SpaceList as SpacesList,
	SpacesDropdownError as SpacesError,
} from '@confluence/spaces-dropdown';
import { SpaceItemView } from '@confluence/entity-view';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { SPACE_OVERVIEW } from '@confluence/named-routes';
import { START_TOUCH } from '@confluence/navdex';
import { CreateSpaceButton } from '@confluence/create-space/entry-points/createSpace';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';

import SpacesEmptyState from '../RightPanel/img/SpacesEmptyState.svg';
import { LoadingDone } from '../RightPanel/LoadingListener/LoadingDone';
import { HOME_OVERVIEW_SPACES_SECTION_SEGMENT } from '../../perf.config';

import {
	LeftPanelHeaderContainer,
	LeftPanelHeaderWrapper,
	LeftPanelListItemLink,
} from './LeftPanelStyledComponents';

const i18n = defineMessages({
	spacesHeader: {
		id: 'home.spaces.header',
		defaultMessage: 'Spaces',
		description: "Header for home's right panel spaces widget",
	},
	empty: {
		id: 'home.empty-message',
		defaultMessage: 'Once you view or create spaces, you’ll find them here.',
		description: 'Displayed when there are no spaces to show in spaces widget',
	},
	spaceStarred: {
		id: 'home.spaces.space.starred',
		defaultMessage: 'starred',
		description: 'Indicate that the space is starred',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SpacesContentContainer = styled.div({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: `${token('space.400', '32px')} ${token('space.0', '0px')} ${token('space.100', '8px')}`,
	width: '100%',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-csstargetingattr="ListItemPrimarySecondary"]': {
		display: 'var(--homeNavCollapsedDisplay)',
		paddingLeft: `${token('space.150', '12px')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-csstargetingattr="ListItemEdit"]': {
		display: 'var(--homeNavCollapsedDisplay)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-csstargetingattr="ListItemActions"]': {
		display: 'var(--homeNavCollapsedDisplay)',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapper = styled.div({
	display: 'block',
	padding: `${token('space.075', '6px')} ${token('space.100', '8px')} ${token(
		'space.075',
		'6px',
	)} ${token('space.150', '12px')}`,
	fontWeight: token('font.weight.regular'),
});

type SpaceContentProps = {
	handleSpaceItemClick: (position: number) => void;
	intl: IntlShape;
};

type SpacesProps = {
	onLoad?: () => void;
};

const totalSpacesShown = 6;

const renderSpacesItems = (spaces, handleSpaceItemClick, intl) => {
	return spaces.map((space, index) => {
		const isFavourited = space?.currentUser?.isFavourited || false;
		const linkLabel = isFavourited
			? `${space.name}, ${intl.formatMessage(i18n.spaceStarred)}`
			: space.name;

		return (
			<SpacesItemContainer key={space.id} role="listitem">
				<LeftPanelListItemLink
					onClick={() => handleSpaceItemClick(index)}
					name={SPACE_OVERVIEW.name}
					params={{ spaceKey: space.alias || space.key }}
					aria-label={linkLabel}
				/>
				<ItemWrapper>
					<SpaceItemView
						space={space}
						spaceIconSize="xsmall"
						analytics={{
							source: 'homeScreen',
							attributes: {
								spaceId: space.id,
							},
						}}
						tooltipPosition="top"
						buttonSize="small"
					/>
				</ItemWrapper>
			</SpacesItemContainer>
		);
	});
};

const SpacesContent: FC<SpaceContentProps> = ({ handleSpaceItemClick, intl }) => {
	const { starredSpaces, recentSpaces, errors, loading } = useSpacesData();

	// copying nav spaces dropdown empty, error, and loading UI since we don't have designs for these yet.
	if (errors.length > 0) {
		return (
			<Fragment>
				<SpacesError error={errors[0]} />
				<ExperienceFailure name={HOME_SPACES_EXPERIENCE} error={errors[0]} />
			</Fragment>
		);
	}

	if (!loading && starredSpaces.length === 0 && recentSpaces.length === 0) {
		return (
			<EmptyContainer>
				<img src={SpacesEmptyState} alt="" />
				<FormattedMessage {...i18n.empty} />
				<ExperienceSuccess name={HOME_SPACES_EXPERIENCE} />
			</EmptyContainer>
		);
	}

	if (loading) {
		return (
			<LoadingContainer>
				<Spinner size="large" />
			</LoadingContainer>
		);
	}
	const starredSpacesItems = starredSpaces.slice(0, totalSpacesShown);
	const recentSpacesItems = recentSpaces.slice(0, totalSpacesShown - starredSpacesItems.length);
	const spaces = [...starredSpacesItems, ...recentSpacesItems];

	return (
		<SpacesList role="list">
			{renderSpacesItems(spaces, handleSpaceItemClick, intl)}
			<PageSegmentLoadEnd metric={HOME_OVERVIEW_SPACES_SECTION_SEGMENT} />
			<ExperienceSuccess name={HOME_SPACES_EXPERIENCE} />
		</SpacesList>
	);
};

export const LeftPanelSpaces: FC<SpacesProps> = memo(({ onLoad }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();

	const handleSpaceItemClick = useCallback(
		(position: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'homeScreen',
					action: 'clicked',
					actionSubject: 'homeLink',
					actionSubjectId: 'spacesSection',
					type: 'ui',
					attributes: {
						ranking: position,
						navdexPointType: START_TOUCH,
						position: 'leftRail',
						homeVersion: 'visualRefresh',
						isGuest,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isGuest],
	);

	return (
		<>
			<SpacesContentContainer>
				<LeftPanelHeaderWrapper>
					<LeftPanelHeaderContainer>
						<Heading size="xxsmall" as="h2">
							<FormattedMessage {...i18n.spacesHeader} />
						</Heading>
						<CreateSpaceButton appearance="plus" source="homeScreen" />
					</LeftPanelHeaderContainer>
				</LeftPanelHeaderWrapper>
				<SpacesContent handleSpaceItemClick={handleSpaceItemClick} intl={intl} />
			</SpacesContentContainer>
			{onLoad && <LoadingDone onLoad={onLoad} />}
		</>
	);
});
